/**
 * Chip — 누를 수 있는 작은 알약/사각 요소.
 *
 * 기존 클래스를 그대로 소비한다. 새 스타일링 방식을 도입하지 않는 이유:
 *   ① 바닐라(components.css)와 시각 패리티가 유지된다 — 컷오버 전까지 프로덕션은 바닐라다
 *   ② CSS 한 파일에서 계속 고칠 수 있다
 *   ③ 이 치환으로 화면이 1px도 바뀌지 않는다 (순수 리팩터)
 *
 * variant 는 '어디에 쓰이나'가 아니라 '어떻게 생겼나'로 나눈다.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ChipVariant =
  | "next"    // 꼬리질문 — 누르면 바로 전송. 알약 · navy 테두리
  | "choice"  // 선택지([[CHOICES]]) — 생김새는 next 와 거의 같다 (아래 주석)
  | "quick"   // 빠른 실행 팝오버 안의 질문
  | "file";   // 첨부 파일 — 사각, 지우기 버튼을 품는다

// ⚠️ next-chip 과 choice-chip 은 거의 같지만 두 가지가 다르다:
//    · choice-chip 에만 :disabled(opacity .45) 와 .picked 가 있다
//    · next-chip 에만 모바일(≤760px) 축소 규칙이 있다
//    지금 합치면 화면이 바뀌므로 분리해 둔다. 합칠지는 스토리에서 보고 정한다.
const CLASS: Record<ChipVariant, string> = {
  next: "next-chip",
  choice: "choice-chip",
  quick: "chip",
  file: "file-chip",
};

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ChipVariant;
  /** 선택됨 — choice 에서만 스타일이 있다 (.picked) */
  selected?: boolean;
  children: ReactNode;
}

/**
 * 누를 수 있는 작은 알약/사각 요소 — 기존 CSS 클래스를 그대로 소비한다.
 *
 * 어디에 쓰였나:
 * - 답변 끝 꼬리질문·되묻기 선택지 — `next`·`choice` · MessageList.tsx
 * - 빠른 실행(⚡) 팝오버의 질문 — `quick` · Composer.tsx
 * - 첨부 파일 — `file` · Composer.tsx
 */
export function Chip({ variant = "next", selected, className, children, ...rest }: ChipProps) {
  const cls = [CLASS[variant], selected && "picked", className].filter(Boolean).join(" ");
  return (
    <button type="button" className={cls} aria-pressed={selected} {...rest}>
      {children}
    </button>
  );
}
